import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Paperclip, CornerRightUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import * as agentService from '../../services/agentService';

/**
 * AgentMentionInput — textarea with @mention autocomplete and file attachment.
 *
 * Props:
 *   onSend(content, mentions, attachments) — called when user submits
 *   disabled — disables input + send button
 *   placeholder — input placeholder text
 *   disabledLabel — label shown when disabled
 */
const AgentMentionInput = ({ onSend, disabled = false, placeholder, disabledLabel }) => {
  const { authenticatedFetch } = useAuth();
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  const [rawText, setRawText] = useState('');
  const [mentions, setMentions] = useState([]); // [{type, id, display_name}]
  const [attachments, setAttachments] = useState([]); // [{temp_doc_id, file_name}]
  const [uploading, setUploading] = useState(false);

  // Mention autocomplete state
  const [mentionQuery, setMentionQuery] = useState(null); // null or string being typed after @
  const [mentionResults, setMentionResults] = useState([]); // grouped results
  const [mentionLoading, setMentionLoading] = useState(false);
  const [selectedMentionIdx, setSelectedMentionIdx] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(-1);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 96) + 'px'; // max 4 lines ≈ 96px
  }, [rawText]);

  // Fetch mention results when query changes
  useEffect(() => {
    if (mentionQuery === null) {
      setMentionResults([]);
      return;
    }
    setMentionLoading(true);
    setSelectedMentionIdx(0);

    const q = mentionQuery;
    Promise.all([
      agentService.searchClients(authenticatedFetch, q),
      agentService.searchPortfolios(authenticatedFetch, q),
      agentService.searchHoldings(authenticatedFetch, q),
      agentService.searchDocuments(authenticatedFetch, q),
    ])
      .then(([clients, portfolios, holdings, documents]) => {
        const grouped = [];
        if (clients.length) grouped.push({ group: 'Clients', items: clients });
        if (portfolios.length) grouped.push({ group: 'Portfolios', items: portfolios });
        if (holdings.length) grouped.push({ group: 'Artworks', items: holdings });
        if (documents.length) grouped.push({ group: 'Documents', items: documents });
        setMentionResults(grouped);
      })
      .catch(() => setMentionResults([]))
      .finally(() => setMentionLoading(false));
  }, [mentionQuery, authenticatedFetch]);

  // Flat list of all mention results for keyboard navigation
  const flatResults = mentionResults.flatMap((g) => g.items);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setRawText(val);

    // Detect @mention trigger
    const cursor = e.target.selectionStart;
    const textBefore = val.slice(0, cursor);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionStartPos(textBefore.lastIndexOf('@'));
    } else {
      setMentionQuery(null);
      setMentionStartPos(-1);
    }
  }, []);

  const insertMention = useCallback((item) => {
    const before = rawText.slice(0, mentionStartPos);
    const after = rawText.slice(textareaRef.current?.selectionStart || rawText.length);
    const chip = `@${item.display_name} `;
    const newText = before + chip + after;
    setRawText(newText);
    setMentions((prev) => [...prev, { type: item.type, id: item.id, display_name: item.display_name }]);
    setMentionQuery(null);
    setMentionStartPos(-1);
    setMentionResults([]);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [rawText, mentionStartPos]);

  const handleKeyDown = useCallback((e) => {
    if (mentionQuery !== null && flatResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIdx((i) => Math.min(i + 1, flatResults.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (flatResults[selectedMentionIdx]) {
          insertMention(flatResults[selectedMentionIdx]);
        }
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }

    // Submit on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionQuery, flatResults, selectedMentionIdx, insertMention]);

  const handleSend = useCallback(() => {
    const trimmed = rawText.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed, mentions, attachments);
    setRawText('');
    setMentions([]);
    setAttachments([]);
    setMentionQuery(null);
  }, [rawText, mentions, attachments, disabled, onSend]);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploading(true);
    try {
      const result = await agentService.uploadTempDocument(authenticatedFetch, file);
      setAttachments((prev) => [...prev, { temp_doc_id: result.temp_doc_id, file_name: result.file_name }]);
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setUploading(false);
    }
  }, [authenticatedFetch]);

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const isEmpty = !rawText.trim() && attachments.length === 0;

  return (
    <div className="px-3 pb-3">
      {/* Mention dropdown — sits above the floating bar */}
      {mentionQuery !== null && flatResults.length > 0 && (
        <div
          ref={dropdownRef}
          className="mb-2 bg-blue-900/90 border border-white/10 rounded-xl shadow-lg overflow-hidden"
        >
          {mentionResults.map((group) => (
            <div key={group.group}>
              <div className="px-3 py-1 text-xs font-semibold text-white/40 uppercase tracking-wide">
                {group.group}
              </div>
              {group.items.map((item) => {
                const flatIdx = flatResults.indexOf(item);
                return (
                  <button
                    key={item.id}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                      flatIdx === selectedMentionIdx ? 'bg-white/10' : 'hover:bg-white/5'
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(item);
                    }}
                  >
                    <span className="font-medium text-white">{item.display_name}</span>
                    {item.subtitle && (
                      <span className="text-white/40 text-xs">{item.subtitle}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          {mentionLoading && (
            <div className="px-3 py-2 text-xs text-white/40">Searching…</div>
          )}
        </div>
      )}

      {/* Floating input bar */}
      <div className="bg-white/10 rounded-2xl border border-white/10">
        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1 px-3 pt-2.5">
            {attachments.map((a, i) => (
              <div
                key={i}
                className="flex items-center gap-1 bg-white/10 rounded px-2 py-1 text-xs text-white/80"
              >
                <span>📄 {a.file_name}</span>
                <button
                  onClick={() => removeAttachment(i)}
                  className="text-white/50 hover:text-white ml-1"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input row */}
        <div className="flex items-end gap-2 px-3 py-2.5">
          {/* Paperclip */}
          <label className={`flex-shrink-0 cursor-pointer p-1 text-white/50 hover:text-white transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            <Paperclip size={16} />
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.tiff" className="hidden" onChange={handleFileChange} />
          </label>

          {/* Textarea */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={rawText}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={
                disabled
                  ? (disabledLabel || 'Agent is working…')
                  : (placeholder || 'Ask anything about your collections…')
              }
              rows={1}
              disabled={disabled}
              className={`w-full resize-none text-sm bg-transparent border-none outline-none leading-5 ${
                disabled
                  ? 'text-white/30 placeholder-white/30 cursor-not-allowed animate-pulse'
                  : 'text-white placeholder-white/40'
              }`}
              style={{ maxHeight: '96px', overflowY: 'auto' }}
            />
          </div>

          {/* Send button — spins when agent is working */}
          <button
            onClick={handleSend}
            disabled={disabled || isEmpty}
            className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
              !disabled && !isEmpty ? 'bg-white/20 text-white hover:bg-white/30' : ''
            }`}
          >
            {disabled ? (
              <div
                className="w-4 h-4 bg-white/60 rounded-sm animate-spin"
                style={{ animationDuration: '3s' }}
              />
            ) : (
              <CornerRightUp
                size={16}
                className={isEmpty ? 'text-white/20' : 'text-white'}
              />
            )}
          </button>
        </div>
      </div>

    </div>
  );
};

export default AgentMentionInput;
